import { themeAtom } from "../../../shared/atoms";
import { ExcelGridRow } from "../../../shared/excel-grid/models/excel-grid.models";
import { ADMIN_CERTIFICATES_KPIS_ENVIRONMENT_OPTIONS } from "../../models/admin-kpis.models";
import type {
  AdminCertificatesKpisRequest,
  AdminCertificatesKpisEnvironment,
  AdminCertificatesKpisPeriodBalticQuestionnaireStatusCode,
  AdminCertificatesKpisPeriodCertificateStatusCode,
  AdminCertificatesKpisResponse,
  AdminKpisFilters,
  AdminKpisLoadingState,
  AdminKpisMissingCertificatesTableFilter,
  AdminKpisTrendWindow,
} from "../../models/admin-kpis.models";
import { getAdminCertificatesKpis, getAdminCertificatesKpisErrorMessage } from "../../services/admin-kpis.service";
import {
  ADMIN_KPIS_CHANGED_NAME_COLUMNS,
  ADMIN_KPIS_DOCUMENT_COLUMNS,
  DEFAULT_ADMIN_KPIS_TREND_WINDOW,
  DEFAULT_ADMIN_KPIS_FILTERS,
  EMPTY_ADMIN_CERTIFICATES_KPIS_RESPONSE,
  countVesselsWithDocumentAlert,
  filterAdminKpisDocuments,
  filterMissingCertificatesByLegend,
  getAdminKpisTrendSegment,
  getMissingCertificateColumnsWithTruncatedDocumentLists,
  getMissingCertificateCountLabel,
  printAdminKpisPdfExport,
  toAdminKpisChangedNameGridRows,
  toAdminKpisDocumentGridRows,
  toAdminKpisMissingCertificateGridRows,
} from "../../utils/admin-kpis.utils";
import CertificatesKpisEnvironmentSelector from "./CertificatesKpisEnvironmentSelector";
import CertificatesKpisVesselTypesSelector from "./CertificatesKpisVesselTypesSelector";
import AdminKpisFilterPanel from "./AdminKpisFilterPanel";
import AdminKpisGridSection from "./AdminKpisGridSection";
import AdminKpisPdfExportButton from "./AdminKpisPdfExportButton";
import AdminKpisPdfExportView from "./AdminKpisPdfExportView";
import AdminKpisPieSection from "./AdminKpisPieSection";
import AdminKpisTrendLinePanel from "./AdminKpisTrendLinePanel";
import { useAtomValue } from "jotai";
import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

const AdminCertificatesKpisPanel = () => {
  const theme = useAtomValue(themeAtom);
  const [response, setResponse] = useState<AdminCertificatesKpisResponse>(EMPTY_ADMIN_CERTIFICATES_KPIS_RESPONSE);
  const [filters, setFilters] = useState<AdminKpisFilters>(DEFAULT_ADMIN_KPIS_FILTERS);
  const [selectedEnvironment, setSelectedEnvironment] = useState<AdminCertificatesKpisEnvironment | null>(null);
  const [selectedSourceVesselTypes, setSelectedSourceVesselTypes] = useState<string[] | null>(null);
  const [activeMissingCertificatesTableFilter, setActiveMissingCertificatesTableFilter] =
    useState<AdminKpisMissingCertificatesTableFilter | null>(null);
  const [isMissingCertificatesTableVisible, setIsMissingCertificatesTableVisible] = useState(false);
  const [selectedTrendWindow, setSelectedTrendWindow] =
    useState<AdminKpisTrendWindow>(DEFAULT_ADMIN_KPIS_TREND_WINDOW);
  const [loadingState, setLoadingState] = useState<AdminKpisLoadingState>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const deferredFilters = useDeferredValue(filters);

  const loadKpis = useCallback(async (request: AdminCertificatesKpisRequest = {}) => {
    setLoadingState("loading");
    setErrorMessage(null);

    try {
      const nextResponse = await getAdminCertificatesKpis(request);
      setResponse(nextResponse);
      setSelectedEnvironment(nextResponse.selectedEnvironment);
      setSelectedSourceVesselTypes(nextResponse.selectedVesselTypes);
      setLoadingState("ready");
    } catch (error) {
      setErrorMessage(getAdminCertificatesKpisErrorMessage(error));
      setLoadingState("error");
    }
  }, []);

  useEffect(() => {
    void loadKpis();
  }, [loadKpis]);

  const filteredDocuments = useMemo(
    () => filterAdminKpisDocuments(response.documents, deferredFilters),
    [deferredFilters, response.documents],
  );
  const documentRows = useMemo(() => toAdminKpisDocumentGridRows(filteredDocuments), [filteredDocuments]);
  const visibleMissingCertificates = useMemo(
    () => filterMissingCertificatesByLegend(response.missingCertificates, activeMissingCertificatesTableFilter),
    [activeMissingCertificatesTableFilter, response.missingCertificates],
  );
  const missingCertificateRows = useMemo(
    () => toAdminKpisMissingCertificateGridRows(visibleMissingCertificates),
    [visibleMissingCertificates],
  );
  const missingCertificateColumns = useMemo(() => getMissingCertificateColumnsWithTruncatedDocumentLists(), []);
  const missingCertificateCountLabel = useMemo(
    () =>
      getMissingCertificateCountLabel(
        activeMissingCertificatesTableFilter,
        visibleMissingCertificates.length,
        response,
      ),
    [activeMissingCertificatesTableFilter, response, visibleMissingCertificates.length],
  );
  const changedNameRows = useMemo(
    () => toAdminKpisChangedNameGridRows(response.periodTcChangedNames),
    [response.periodTcChangedNames],
  );
  const vesselsWithAlerts = useMemo(() => countVesselsWithDocumentAlert(filteredDocuments), [filteredDocuments]);
  const environmentOptions =
    response.environmentOptions.length > 0 ? response.environmentOptions : ADMIN_CERTIFICATES_KPIS_ENVIRONMENT_OPTIONS;
  const activeEnvironment = selectedEnvironment ?? response.selectedEnvironment;
  const activeSourceVesselTypes = selectedSourceVesselTypes ?? response.selectedVesselTypes;
  const requestVesselTypes = getSourceVesselTypesForKpiRequest(
    activeSourceVesselTypes,
    response.sourceVesselTypeOptions,
    response.defaultExcludedVesselTypes,
  );
  const isDefaultSourceVesselTypeSelection = areDefaultSourceVesselTypesSelected(
    activeSourceVesselTypes,
    response.sourceVesselTypeOptions,
    response.defaultExcludedVesselTypes,
  );
  const activeMissingCertificatesSegment = useMemo(
    () =>
      getAdminKpisTrendSegment(
        activeMissingCertificatesTableFilter,
        response.periodFleetCertificateStatus,
        response.periodFleetBalticQuestionnaireStatus,
      ),
    [
      activeMissingCertificatesTableFilter,
      response.periodFleetBalticQuestionnaireStatus,
      response.periodFleetCertificateStatus,
    ],
  );
  const shouldShowTrendLine = Boolean(activeMissingCertificatesTableFilter) && isDefaultSourceVesselTypeSelection;
  const shouldShowMissingCertificatesTable =
    isMissingCertificatesTableVisible &&
    (!activeMissingCertificatesTableFilter || (activeMissingCertificatesSegment?.value ?? 0) > 0);
  const activePdfTrendFilter = shouldShowTrendLine ? activeMissingCertificatesTableFilter : null;

  const handleRefresh = useCallback(() => {
    void loadKpis({ environment: selectedEnvironment ?? undefined, vesselTypes: requestVesselTypes });
  }, [loadKpis, requestVesselTypes, selectedEnvironment]);
  const handleFiltersChange = useCallback((nextFilters: AdminKpisFilters) => {
    setFilters(nextFilters);
  }, []);
  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_ADMIN_KPIS_FILTERS);
  }, []);

  const handleEnvironmentChange = useCallback(
    (environment: AdminCertificatesKpisEnvironment) => {
      setSelectedEnvironment(environment);
      setFilters(DEFAULT_ADMIN_KPIS_FILTERS);
      setActiveMissingCertificatesTableFilter(null);
      setIsMissingCertificatesTableVisible(false);
      void loadKpis({ environment, vesselTypes: requestVesselTypes });
    },
    [loadKpis, requestVesselTypes],
  );

  const handleSourceVesselTypesChange = useCallback(
    (vesselTypes: string[]) => {
      const nextRequestVesselTypes = getSourceVesselTypesForKpiRequest(
        vesselTypes,
        response.sourceVesselTypeOptions,
        response.defaultExcludedVesselTypes,
      );
      setSelectedSourceVesselTypes(vesselTypes);
      setFilters(DEFAULT_ADMIN_KPIS_FILTERS);
      setActiveMissingCertificatesTableFilter(null);
      setIsMissingCertificatesTableVisible(false);
      void loadKpis({ environment: selectedEnvironment ?? undefined, vesselTypes: nextRequestVesselTypes });
    },
    [loadKpis, response.defaultExcludedVesselTypes, response.sourceVesselTypeOptions, selectedEnvironment],
  );

  const handlePeriodCertificateSegmentClick = useCallback((code: string) => {
    setIsMissingCertificatesTableVisible(true);
    setActiveMissingCertificatesTableFilter({
      source: "periodCertificate",
      code: code as AdminCertificatesKpisPeriodCertificateStatusCode,
    });
  }, []);

  const handlePeriodBalticQuestionnaireSegmentClick = useCallback((code: string) => {
    setIsMissingCertificatesTableVisible(true);
    setActiveMissingCertificatesTableFilter({
      source: "periodBalticQuestionnaire",
      code: code as AdminCertificatesKpisPeriodBalticQuestionnaireStatusCode,
    });
  }, []);

  const handleShowAllMissingCertificates = useCallback(() => {
    setIsMissingCertificatesTableVisible(true);
    setActiveMissingCertificatesTableFilter(null);
  }, []);

  const handleHideMissingCertificatesTable = useCallback(() => {
    setIsMissingCertificatesTableVisible(false);
  }, []);

  const handlePdfExport = useCallback(() => {
    printAdminKpisPdfExport();
  }, []);

  return (
    <>
      <main
        data-test="admin-kpis-page"
        className={twMerge(
          "admin-kpis-screen-content flex min-h-0 min-w-0 flex-1 overflow-hidden",
          theme.home.tailwind.textDefault,
        )}
      >
        <AdminKpisFilterPanel
          filters={filters}
          filterOptions={response.filterOptions}
          isRefreshing={loadingState === "loading"}
          onChange={handleFiltersChange}
          onRefresh={handleRefresh}
          onReset={handleResetFilters}
        />
        <div className="min-w-0 flex-1 overflow-y-auto" data-test="admin-kpis-content">
          <div className="px-8 py-7">
            <header className="mb-6 grid items-end gap-4 xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
              <div className="flex min-w-0 flex-wrap items-end justify-start gap-3">
                <CertificatesKpisEnvironmentSelector
                  value={activeEnvironment}
                  options={environmentOptions}
                  loading={loadingState === "loading"}
                  onChange={handleEnvironmentChange}
                />
                <CertificatesKpisVesselTypesSelector
                  value={activeSourceVesselTypes}
                  options={response.sourceVesselTypeOptions}
                  loading={loadingState === "loading"}
                  onChange={handleSourceVesselTypesChange}
                />
              </div>
              <h1 className="text-center text-4xl font-semibold leading-none">Certificates Management</h1>
              <div className="flex justify-start xl:justify-self-end">
                <AdminKpisPdfExportButton disabled={loadingState === "loading"} onClick={handlePdfExport} />
              </div>
            </header>

            {loadingState === "error" && (
              <div
                data-test="admin-kpis-error"
                className="mb-5 border border-[#BF0002] bg-[#FFF1F1] px-4 py-3 text-sm font-medium text-[#8F0002]"
              >
                {errorMessage}
              </div>
            )}

            {loadingState === "loading" && response.documents.length === 0 ? (
              <div
                data-test="admin-kpis-loading"
                className="flex min-h-[420px] items-center justify-center bg-white text-sm shadow-sm dark:bg-gray-800"
              >
                Loading KPIs...
              </div>
            ) : (
              <div className="grid gap-7">
                <div className="grid gap-5 2xl:grid-cols-2">
                  <AdminKpisPieSection
                    title="Period Fleet Certificates"
                    section={response.periodFleetCertificateStatus}
                    dataTest="admin-kpis-period-certificates"
                    showSegmentPercentLabels
                    activeSegmentCode={
                      activeMissingCertificatesTableFilter?.source === "periodCertificate"
                        ? activeMissingCertificatesTableFilter.code
                        : null
                    }
                    onSegmentClick={handlePeriodCertificateSegmentClick}
                    onShowAll={handleShowAllMissingCertificates}
                    onHideTable={handleHideMissingCertificatesTable}
                  />
                  <AdminKpisPieSection
                    title="Period Fleet Baltic Questionnaire"
                    section={response.periodFleetBalticQuestionnaireStatus}
                    dataTest="admin-kpis-period-bq"
                    showSegmentPercentLabels
                    activeSegmentCode={
                      activeMissingCertificatesTableFilter?.source === "periodBalticQuestionnaire"
                        ? activeMissingCertificatesTableFilter.code
                        : null
                    }
                    onSegmentClick={handlePeriodBalticQuestionnaireSegmentClick}
                    onShowAll={handleShowAllMissingCertificates}
                    onHideTable={handleHideMissingCertificatesTable}
                  />
                </div>

                {shouldShowTrendLine && (
                  <AdminKpisTrendLinePanel
                    activeFilter={activeMissingCertificatesTableFilter}
                    history={response.dailySnapshotHistory}
                    periodFleetCertificateStatus={response.periodFleetCertificateStatus}
                    periodFleetBalticQuestionnaireStatus={response.periodFleetBalticQuestionnaireStatus}
                    selectedWindow={selectedTrendWindow}
                    onWindowChange={setSelectedTrendWindow}
                  />
                )}

                {shouldShowMissingCertificatesTable && (
                  <AdminKpisGridSection
                    title="Vessels With Missing Certificates"
                    countLabel={missingCertificateCountLabel}
                    rows={missingCertificateRows}
                    columns={missingCertificateColumns}
                    dataTest="admin-kpis-missing-certificates-grid"
                    emptyLabel="No missing certificate rows."
                    gridHeightClassName="max-h-[650px]"
                    virtualized
                    virtualizedMaxHeight={650}
                    useContentColumnWidths={false}
                  />
                )}

                <AdminKpisGridSection
                  title="Documents And Alerts"
                  countLabel={`Number of vessels with an alert: ${vesselsWithAlerts}`}
                  rows={documentRows as ExcelGridRow[]}
                  columns={ADMIN_KPIS_DOCUMENT_COLUMNS}
                  dataTest="admin-kpis-documents-grid"
                  emptyLabel="No documents match the active filters."
                  gridHeightClassName="max-h-[600px]"
                  virtualized
                  virtualizedMaxHeight={600}
                  useContentColumnWidths={false}
                />

                <AdminKpisGridSection
                  title="Period TC Vessels With Changed Names"
                  countLabel={`${response.periodTcChangedNames.length} vessels`}
                  rows={changedNameRows}
                  columns={ADMIN_KPIS_CHANGED_NAME_COLUMNS}
                  dataTest="admin-kpis-changed-names-grid"
                  emptyLabel="No changed vessel names."
                  gridHeightClassName="max-h-[500px]"
                  emptySectionMessage="No Period TC vessels with changed names found."
                  virtualized
                  virtualizedMaxHeight={500}
                  useContentColumnWidths={false}
                />
              </div>
            )}
          </div>
        </div>
      </main>
      <AdminKpisPdfExportView
        environment={activeEnvironment}
        periodFleetCertificateStatus={response.periodFleetCertificateStatus}
        periodFleetBalticQuestionnaireStatus={response.periodFleetBalticQuestionnaireStatus}
        activeTrendFilter={activePdfTrendFilter}
        dailySnapshotHistory={response.dailySnapshotHistory}
        selectedTrendWindow={selectedTrendWindow}
      />
    </>
  );
};

function getSourceVesselTypesForKpiRequest(
  selectedVesselTypes: string[],
  sourceVesselTypeOptions: string[],
  defaultExcludedVesselTypes: string[],
): string[] | undefined {
  return areDefaultSourceVesselTypesSelected(selectedVesselTypes, sourceVesselTypeOptions, defaultExcludedVesselTypes)
    ? undefined
    : selectedVesselTypes;
}

function areDefaultSourceVesselTypesSelected(
  selectedVesselTypes: string[],
  sourceVesselTypeOptions: string[],
  defaultExcludedVesselTypes: string[],
): boolean {
  const selectedVesselTypeValues = new Set(selectedVesselTypes);
  const defaultExcludedVesselTypeValues = new Set(defaultExcludedVesselTypes);
  const defaultSelectedVesselTypes = sourceVesselTypeOptions.filter(
    (vesselType) => !defaultExcludedVesselTypeValues.has(vesselType),
  );

  return (
    selectedVesselTypes.length === defaultSelectedVesselTypes.length &&
    defaultSelectedVesselTypes.every((vesselType) => selectedVesselTypeValues.has(vesselType))
  );
}

export default AdminCertificatesKpisPanel;
